import React, { useEffect, useState, useCallback } from "react";
import {
  useLocation,
  useMatch,
  Routes,
  Route,
  useNavigate,
  Outlet,
} from "react-router-dom";
import { Helmet } from "react-helmet";

import Filter from "../../elements/filter";
import ReportSummary from "../../elements/reportSummary";

import SalesSummary from "./views/salesSummary";
import SessionSummary from "./views/sessionSummary";
import Revcent from "./views/revcent";
import Paytype from "./views/paytype";
import Sales from "./views/sales";
import Item from "./views/item";
import Group from "./views/group";
import hourlySales from "./views/hourlySales";
import RevcentItemSales from "./views/itemByRevcent";
import { useAppContext } from "../../../../AppContext";

import "../style.scss";

function SalesComponent() {
  const {
    filterValue,
    setFilterValue,
    user,
    chimeraserv,
    components,
    preservedSalesData,
    setPreservedSalesData,
    preservedSessionData,
    setPreservedSessionData,
  } = useAppContext();
  const location = useLocation();
  const match = useMatch("/app/sales/*");
  const navigate = useNavigate();

  const [filter, setFilter] = useState(null);
  const [page, setPage] = useState(location.pathname);
  const [isPreservedSession, setIsPreservedSession] = useState(false);

  const routes = [
    {
      name: "Group by Sales Summary",
      path: "summary",
      component: SalesSummary,
    },
    {
      name: "Group by Session Summary",
      path: "session",
      component: SessionSummary,
    },
    {
      name: "Group by Revenue Center",
      path: "revcent",
      component: Revcent,
    },
    {
      name: "Group by Payment Type",
      path: "paytype",
      component: Paytype,
    },
    {
      name: "Group Sales by Checks",
      path: "checks",
      component: Sales,
    },
    {
      name: "Group Item by Sales",
      path: "item",
      component: Item,
    },
    {
      name: "Group Sales by Group Items",
      path: "group",
      component: Group,
    },
    {
      name: "Hourly Sales",
      path: "hourly",
      component: hourlySales,
    },
    {
      name: "Item Sales by Revenue Center",
      path: "revcentItem",
      component: RevcentItemSales,
    },
  ];

  // Check if we should preserve data - ONLY for Sales component
  const shouldPreserveData =
    location.state?.prevUrl?.includes("/app/view") &&
    (preservedSalesData || preservedSessionData) &&
    (location.pathname.includes("/app/sales/checks") ||
      location.pathname.includes("/app/sales/session"));

  // Load preserved data on mount if available
  useEffect(() => {
    // Only restore if we're on the Sales route and coming from view
    if (
      !filter &&
      location.state?.prevUrl?.includes("/app/view") &&
      (location.pathname.includes("/app/sales/checks") ||
        location.pathname.includes("/app/sales/session"))
    ) {
      // Check for preserved data and ensure it has a filter property
      const preservedData = location.pathname.includes("/app/sales/checks")
        ? preservedSalesData
        : preservedSessionData;

      if (preservedData && preservedData.filter) {
        setFilter(preservedData.filter);
        setFilterValue(preservedData.filter);
        setIsPreservedSession(true);
        // Clear the state after using it
        window.history.replaceState({}, "", location.pathname);
      }
    }
  }, [
    preservedSalesData,
    preservedSessionData,
    filter,
    location.state,
    location.pathname,
    setFilterValue,
  ]);

  // Handle location changes
  useEffect(() => {
    if (location.pathname !== page) {
      // Only preserve data for Sales route
      if (
        shouldPreserveData &&
        (location.pathname.includes("/app/sales/checks") ||
          location.pathname.includes("/app/sales/session"))
      ) {
        // Restore preserved data with null checks
        const preservedData = location.pathname.includes("/app/sales/checks")
          ? preservedSalesData
          : preservedSessionData;

        if (preservedData && preservedData.filter) {
          setFilter(preservedData.filter);
          setFilterValue(preservedData.filter);
          setIsPreservedSession(true);
          // Clear the state after using it
          window.history.replaceState({}, "", location.pathname);
        }
      } else if (!location.state?.prevUrl?.includes("/app/view")) {
        // Clear filter for normal navigation (not from view)
        setFilter(null);
        setFilterValue(null);
        setIsPreservedSession(false);
        // Only clear preserved data if not coming from view
        if (
          location.pathname.includes("/app/sales/checks") ||
          location.pathname.includes("/app/sales/session")
        ) {
          setPreservedSalesData(null);
          setPreservedSessionData(null);
        }
      } else {
        // For other routes, just clear normally
        setTimeout(() => {
          setFilter(null);
          setFilterValue(null);
          setIsPreservedSession(false);
        }, 500);
      }

      setPage(location.pathname);
    }
  }, [
    location.pathname,
    location.state,
    page,
    shouldPreserveData,
    preservedSalesData,
    setPreservedSalesData,
    preservedSessionData,
    setPreservedSessionData,
    setFilterValue,
  ]);

  // Set filter and save to context ONLY for Sales route
  const handleSetFilter = useCallback(
    (filters) => {
      // Save to context ONLY if we're on Sales route
      if (location.pathname.includes("/app/sales/checks")) {
        setPreservedSalesData({
          filter: filters,
          timestamp: Date.now(),
          path: location.pathname,
        });
      }
      if (location.pathname.includes("/app/sales/session")) {
        setPreservedSessionData({
          filter: filters,
          timestamp: Date.now(),
          path: location.pathname,
        });
      }

      // Clear current filter first
      setFilter(null);
      setFilterValue(null);
      setIsPreservedSession(false);

      setTimeout(() => {
        setFilter(filters);
        setPage(location.pathname);
        setFilterValue(filters);
      }, 500);
    },
    [
      location.pathname,
      setPreservedSalesData,
      setPreservedSessionData,
      setFilterValue,
    ]
  );

  // Clear filter function
  const clearFilter = useCallback(() => {
    setFilter(null);
    setFilterValue(null);
    setIsPreservedSession(false);
    // Only clear preserved sales data if we're on sales route
    if (location.pathname.includes("/app/sales/checks")) {
      setPreservedSalesData(null);
    }
    if (location.pathname.includes("/app/sales/session")) {
      setPreservedSessionData(null);
    }
  }, [
    setPreservedSalesData,
    setPreservedSessionData,
    setFilterValue,
    location.pathname,
  ]);

  // Set initial page on mount
  useEffect(() => {
    setPage(location.pathname);
  }, []);

  // Redirect to summary if only /app/sales
  useEffect(() => {
    if (location.pathname.split("/").length === 3) {
      navigate(match.path + "summary", { replace: true });
    }
  }, [location.pathname, match.path, navigate]);

  // Title logic
  const renderTitle = (title) => {
    let name = "Sales";
    if (title) {
      name = title.name + " | Sales";
      if (location.pathname.split("/").length === 3) {
        name = "Sales";
      }
    }
    return (
      <Helmet>
        <title>{name + " | Chimera Solutions | Insights"}</title>
      </Helmet>
    );
  };

  // Find current route title
  let title = routes.find(
    (route) => route.path === location.pathname.split("/")[3]
  );
  if (location.pathname.split("/").length === 3) {
    title = { name: "Group by Sales Summary" };
  }

  // Check if we're in a preserved session for Sales route only
  const showPreservationBanner =
    (shouldPreserveData || isPreservedSession) &&
    (location.pathname.includes("/app/sales/checks") ||
      location.pathname.includes("/app/sales/session"));

  return (
    <section>
      {renderTitle(title)}
      <Filter
        location={location.pathname}
        setFilter={handleSetFilter}
        key={location.pathname}
      />

      <div className="report">
        <div className="viewTitle">{title ? title.name : <div />}</div>
        <Routes>
          <Route
            path={location.pathname}
            element={
              filter ? (
                <section>
                  <ReportSummary filter={filter} />
                  <SalesSummary
                    user={user}
                    chimeraserv={chimeraserv}
                    filter={filter}
                  />
                </section>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    margin: "20px 0",
                  }}
                >
                  <h4>No Report Generated</h4>
                  <p>
                    Please select filters and click "Generate Report" to view
                    sales data.
                  </p>
                </div>
              )
            }
          />
          {routes.map((route, key) => (
            <Route
              key={key}
              path={route.path}
              element={
                filter ? (
                  <section>
                    <ReportSummary filter={filter} />
                    <route.component
                      user={user}
                      chimeraserv={chimeraserv}
                      filter={filter}
                      preserveData={
                        (route.component === Sales &&
                          location.pathname.includes("/app/sales/checks")) ||
                        (route.component === SessionSummary &&
                          location.pathname.includes("/app/sales/session"))
                          ? isPreservedSession || showPreservationBanner
                          : false
                      }
                      key={`${route.path}-${filter?.timestamp || "default"}`}
                    />
                  </section>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      margin: "20px 0",
                    }}
                  >
                    <h4>No Report Generated</h4>
                    <p>
                      Please select filters and click "Generate Report" to view{" "}
                      {route.name.toLowerCase()}.
                    </p>
                  </div>
                )
              }
            />
          ))}
          <Route
            path="*"
            element={
              components?.setting?.[0]?.component ? (
                React.createElement(components.setting[0].component)
              ) : (
                <div />
              )
            }
          />
        </Routes>
      </div>
    </section>
  );
}

export default SalesComponent;
